import _ from 'underscore'
import React from 'react'
import styled from 'styled-components'

import Checkbox from 'components/Checkbox'
import Tooltip from 'components/Tooltip'

import { color } from 'styles/colors'

import { DeviceMaintenanceTrendGroup, DeviceMetric } from 'types/DeviceMetric'

type Props = {
  metricAndGroupList: Array<DeviceMaintenanceTrendGroup | DeviceMetric>
  showMetricSelection: boolean,
  selectedMetrics: string[]
  handleMetricSelection: (metricName: string) => void
}

const MetricInfoHeaderList = ({ metricAndGroupList, showMetricSelection, selectedMetrics, handleMetricSelection }: Props) => {
  return (
    <S.Header>
      {metricAndGroupList.map((item) => {
        if (!item) { return null }

        const isMetric = 'metricId' in item
        const renderMetricCheckBox = () => (
          <Tooltip title={selectedMetrics.includes(item.name) ? 'Unselect to remove metric from graph' : 'Select to add metric to graph'} placement="top">
            <S.CheckboxContainer onClick={e =>
              e.stopPropagation()
            }
            >
              <S.Checkbox
                label={item.name}
                checked={selectedMetrics.includes(item.name)}
                onChange={() => {
                  handleMetricSelection(item.name)
                }}
              />
            </S.CheckboxContainer>
          </Tooltip>
        )

        return (
          <S.Title key={item.name}>
            <S.TitleUnitContainer>
              {isMetric && <S.Subtitle><S.MetricColor bgColor={item.color} /></S.Subtitle>}
              {showMetricSelection ? renderMetricCheckBox() : <u>{item.name}</u>}
              {item.unit && <S.Unit>({item.unit})</S.Unit>}
            </S.TitleUnitContainer>

            {!isMetric &&
              <S.GroupContainer>
                {_.sortBy(item.metrics, 'sortPosition').map(({ metricId, name = '', color = '' }) => {
                  return (
                    <S.Group key={metricId}>
                      <S.Subtitle aria-label={`${item.name} ${name}`}>
                        <S.MetricColor bgColor={color} />
                        {name}
                      </S.Subtitle>
                    </S.Group>
                  )
                })}
              </S.GroupContainer>
            }
          </S.Title>
        )
      })
      }
    </S.Header>
  )
}

export default MetricInfoHeaderList

const S = {
  Header: styled.div`
    display: grid;
    padding: 3px 0px 10px 0;
    margin-right: 35px;
    grid-template-columns: repeat(auto-fit, minmax(50px, 1fr));
    grid-column-gap: 30px;
    background: ${color('paneBackground')};
    align-items: flex-start;
  `,
  Title: styled.h3`
    display: flex;
    text-transform: capitalize;
    margin: 0;
    width: 100%;
    flex-wrap: wrap;
    gap: 10px;
  `,
  TitleUnitContainer: styled.div`
    display: flex;
    text-transform: capitalize;
    flex-wrap: nowrap;
    align-items: center;
    overflow: hidden;
    flex-shrink: 1;
    min-width: 0;
  `,
  Unit: styled.span`
    text-transform: none;
    flex-shrink: 0;
    padding-left: 5px;
  `,
  Subtitle: styled.span`
    display: flex;
    align-items: center;
  `,
  MetricColor: styled.span.withConfig({
    shouldForwardProp: (prop) => !['bgColor'].includes(prop)
  }) <{ bgColor?: string }>`
    margin: -3px 10px 0 0;
    height: 10px;
    width: 10px;
    border-radius: 50%;
    background-color: ${({ bgColor }) => bgColor || 'transparent'};
    display: inline-block;
  `,
  GroupContainer: styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-left: 5px;
    flex-shrink: 1;
    overflow: hidden;
    min-width: 0;
  `,
  Group: styled.span`
    padding-right: 5px;
  `,
  CheckboxContainer: styled.div`
    display: inline-block;
    overflow: hidden;
    flex-shrink: 1;
    min-width: 0;
  `,
  Checkbox: styled(Checkbox)`
    display: flex;
    margin: 0;
    label {
      flex-shrink: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      text-decoration: underline;
      min-width: 0;
    }
  `
}
